import { createPdfHelpers, uploadAndRegisterPdf } from './gestion-tecnica-documentos'
import { HeatStressCalculationRow } from './gestion-tecnica-estres-termico'
import { TasaMetabolica } from './estres-termico'

const TASA_METABOLICA_LABEL: Record<TasaMetabolica, string> = {
  reposo: 'Reposo',
  ligera: 'Ligera',
  moderada: 'Moderada',
  pesada: 'Pesada',
  muy_pesada: 'Muy pesada',
}

// Genera el reporte técnico PDF de una evaluación de Estrés Térmico (índice
// WBGT), siguiendo el mismo patrón que generateNoiseReportPDF():
// createPdfHelpers() para el flujo de texto + uploadAndRegisterPdf() para
// subir el blob y registrarlo en sso_documents.
//
// checklist_item_id: 'GT-15' — mismo ítem que Ruido e Iluminación
// ("Higiene Industrial — Agentes Físicos"), unificado el 2026-09-18 (ver
// fix de checklist_item_id GT-18/GT-19 → GT-15).
export async function generateHeatStressReportPDF(
  calculo: HeatStressCalculationRow,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const helpers = createPdfHelpers()
  const { txt, hline, doc } = helpers

  const { inputs, resultado } = calculo

  // a. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Reporte de Evaluación de Estrés Térmico — Índice WBGT (GT-15)', 12, 'bold', 'center', 0, 6)
  txt(calculo.descripcion, 11, 'bold', 'center', 0, 6)
  hline()

  txt(
    'Fuente metodológica: NTP 1189 (INSST) — Evaluación del estrés térmico. Índice WBGT, y norma UNE-EN ISO 7243:2017.',
    9, 'italic', 'left', 0, 4
  )

  // b. Datos de entrada
  txt('Datos de entrada', 11, 'bold', 'left', 0, 4)
  txt(
    `Ambiente: ${inputs.ambiente === 'interior' ? 'Interior (sin radiación solar directa)' : 'Exterior (con radiación solar directa)'}`,
    9, 'normal', 'left', 0, 2
  )
  txt(`Temperatura de globo (tg): ${inputs.tg} °C`, 9, 'normal', 'left', 4, 2)
  txt(`Temperatura húmeda natural (thn): ${inputs.thn} °C`, 9, 'normal', 'left', 4, 2)
  if (inputs.ambiente === 'exterior') {
    txt(`Temperatura del aire (ta): ${inputs.ta ?? 0} °C`, 9, 'normal', 'left', 4, 2)
  }
  txt(`Tasa metabólica de la tarea: ${TASA_METABOLICA_LABEL[inputs.tasaTrabajo]}`, 9, 'normal', 'left', 0, 2)
  txt(`Porcentaje de tiempo trabajado (vs. reposo): ${inputs.porcentajeTiempoTrabajado}%`, 9, 'normal', 'left', 0, 2)
  txt(`Trabajador aclimatado al calor: ${inputs.aclimatado ? 'sí' : 'no'}`, 9, 'normal', 'left', 0, 4)
  hline()

  // c. Resultado
  txt('Resultado del cálculo', 11, 'bold', 'left', 0, 4)
  txt(`WBGT calculado: ${resultado.wbgt.toFixed(1)} °C`, 10, 'bold', 'left', 0, 2)
  txt(`Tasa metabólica promedio (M): ${resultado.mPromedio.toFixed(0)} W/m²`, 9, 'normal', 'left', 0, 2)
  txt(`Categoría resultante: ${TASA_METABOLICA_LABEL[resultado.categoria]}`, 9, 'normal', 'left', 0, 2)
  txt(
    `Límite WBGT aplicable (${TASA_METABOLICA_LABEL[resultado.categoria]}, ${inputs.aclimatado ? 'aclimatado' : 'no aclimatado'}): ${resultado.limite} °C`,
    9, 'normal', 'left', 0, 4
  )
  txt(`Cumplimiento: ${resultado.cumple ? 'CUMPLE' : 'NO CUMPLE'}`, 11, 'bold', 'left', 0, 4)
  hline()

  // d. Disclaimer — mismo criterio que ROSA/RULA/REBA: el PDF documenta
  // exactamente lo que ve el usuario en pantalla, no una versión distinta.
  // Nota: el texto sobre las limitaciones del método WBGT es una
  // paráfrasis fiel de cómo la norma describe el método (método de
  // cribado / primera aproximación), no una cita textual verificada
  // carácter por carácter — si se dispone del texto exacto de la NTP 1189,
  // reemplazar por la cita literal entre comillas.
  txt(
    'Método según NTP 1189 (INSST) — Evaluación del estrés térmico. Índice WBGT, y norma UNE-EN ISO 7243:2017. ' +
    'El índice WBGT es un método de cribado (primera aproximación) para estimar el riesgo de estrés térmico por calor; ' +
    'no es un método preciso, y ante un resultado cercano al límite o dudas sobre su aplicabilidad al puesto evaluado ' +
    'corresponde un análisis más detallado (p. ej. índice de sobrecarga térmica PHS, ISO 7933). ' +
    'Este reporte no reemplaza el juicio de un profesional de seguridad y salud ocupacional para la evaluación completa del puesto.',
    8, 'italic', 'left', 0, 4
  )

  const blob = doc.output('blob')
  const filename = `EstresTermico_${calculo.descripcion.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${calculo.id}-estres-termico`, blob, filename, {
    category: 'procedimiento',
    title: `Reporte de Estrés Térmico - ${calculo.descripcion}`,
    description: 'Generado automáticamente desde Metodología de Estrés Térmico (Índice WBGT)',
    checklist_item_id: 'GT-15',
    uploaded_by: uploadedBy,
  })
}
